// src/components/atoms/avatar/SphereAvatar.tsx
import React, { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { useTranslation } from 'localization';
import './sphere-avatar.scss';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { getStatusColor, ExtendedStatusValue } from '../../../utils/statusColors';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import type { GlyphColor } from '../../../types/glyph';

// Status value type for sparkle indicator
export type SphereStatusValue = ExtendedStatusValue;

// Sparkle icon SVG component
const SparkleIcon: React.FC<{ color: string; size: number }> = ({ color, size }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 27 26"
    fill="none"
    aria-hidden="true"
    className="sphere-avatar-sparkle__icon"
  >
    <path
      d="M6.72039 12.8585L11.5481 10.986L13.3037 5.83639C13.4867 5.29933 14.1988 5.29933 14.3819 5.83639L16.1374 10.986L20.9651 12.8585C21.4687 13.0538 21.4687 13.8134 20.9651 14.0087L16.1374 15.8812L14.3819 21.0308C14.1988 21.5679 13.4867 21.5679 13.3037 21.0308L11.5481 15.8812L6.72039 14.0087C6.2169 13.8134 6.2169 13.0538 6.72039 12.8585Z"
      fill={color}
      stroke={color}
    />
  </svg>
);

interface SphereAvatarProps {
  glyph?: string | null;
  glyphColor?: GlyphColor;
  sphereId?: string; // sphereId to fetch image
  sphereName?: string;
  imageId?: string; // imageId to fetch image
  /**
   * The picture to paint, when the caller already holds it (a URL, or `null` for none). The
   * avatar then shows it instead of the picture it read for `sphereId`, so a picture the caller
   * just changed shows without a remount.
   */
  pictureUrl?: string | null;
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge'; // Extended sizes if needed
  shape?: 'circle' | 'square'; // PrimeReact supports 'circle' and 'square'
  className?: string;
  showInitials?: boolean;
  /** The accessible name. Defaults to the sphere's name, else a generic localised one. */
  altText?: string;
  /**
   * Hides the avatar from assistive technology, for a site where the sphere's name is visible next
   * to it (a list row, a picker option, a breadcrumb), so the name is not announced twice. Ignored
   * with `onClick`: a control needs a name.
   */
  decorative?: boolean;
  badge?: React.ReactNode; // For displaying badges
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void; // Click handler
  status?: SphereStatusValue; // Optional status to show sparkle indicator
  onStatusClick?: (event: React.MouseEvent) => void; // Click handler for status sparkle
}

const SphereAvatar: React.FC<SphereAvatarProps> = ({
  glyph,
  glyphColor,
  sphereId,
  sphereName,
  imageId,
  pictureUrl,
  size = 'small',
  shape = 'circle',
  className = '',
  showInitials = false,
  altText,
  decorative = false,
  badge,
  onClick,
  status,
  onStatusClick,
}) => {
  const { t } = useTranslation();
  const accessibleName = altText ?? (sphereName || t('sphere.avatar.altText'));
  const hidden = decorative && !onClick;
  const nameProps = hidden ? { 'aria-hidden': true as const } : { 'aria-label': accessibleName };
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;

  const [imageUrl, setImageUrl] = useState<string>('');
  const [cachedGlyph, setCachedGlyph] = useState<string>('');
  const [cachedGlyphColor, setCachedGlyphColor] = useState<GlyphColor | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  // Changes when the sphere's cache entry is cleared (a write to its picture or glyph, anywhere), so
  // the read below runs again and this avatar shows the new value without a remount.
  const subscribe = useCallback(
    (onChange: () => void) => (sphereId ? globalSphereCache.subscribe(sphereId, onChange) : () => undefined),
    [sphereId]
  );
  const getVersion = useCallback(() => (sphereId ? globalSphereCache.versionOf(sphereId) : ''), [sphereId]);
  const cacheVersion = useSyncExternalStore(subscribe, getVersion, getVersion);

  useEffect(() => {
    let isMounted = true;

    const fetchSphereData = async () => {
      if (!sphereId || !graphClient) return;

      setLoading(true);
      try {
        const cachedData = await globalSphereCache.getSphereData(
          sphereId,
          graphClient
        );

        if (!isMounted) return;

        setImageUrl(cachedData.thumbnailUrl ?? '');

        // Use cached glyph (sphere type icon) when not provided via props
        setCachedGlyph(cachedData.info?.sphere?.properties?.glyph ?? '');
        setCachedGlyphColor(cachedData.info?.sphere?.properties?.glyphColor);
      } catch {
        // Silent fallback - render with whatever props are available
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (sphereId && graphClient) {
      fetchSphereData();
    }

    return () => {
      isMounted = false;
    };
  }, [sphereId, graphClient, cacheVersion]);

  // Resolve effective glyph: prop > cache > undefined
  const effectiveGlyph = glyph || cachedGlyph;
  const effectiveGlyphColor = glyphColor || cachedGlyphColor;
  const shownImageUrl = pictureUrl === undefined ? imageUrl : pictureUrl ?? '';

  // Determine what to display inside the Avatar
  let content: React.ReactNode;

  if (shownImageUrl) {
    content = (
      <Avatar
        image={shownImageUrl}
        shape={shape}
        className={`sphere-avatar ${size} ${className}`}
        imageAlt={hidden ? '' : accessibleName}
        onClick={onClick}
        {...nameProps}
      />
    );
  } else if (showInitials) {
    // Display 2-char initials with deterministic background color
    const initials = getInitials(sphereName);
    const bgColor = getDeterministicColor(sphereId || sphereName);
    content = (
      <Avatar
        label={initials}
        shape={shape}
        className={`sphere-avatar ${size} ${className}`}
        style={{ backgroundColor: bgColor }}
        onClick={onClick}
        {...nameProps}
      />
    );
  } else if (effectiveGlyph) {
    // Display sphere type icon via GlyphAtom
    const glyphClassName = `sphere-avatar ${size} ${className} sphere-avatar--glyph`;
    const glyphNode = <GlyphAtom glyph={effectiveGlyph} glyphColor={effectiveGlyphColor} />;
    // A native button gives the clickable avatar its role, focus and Enter/Space activation.
    content = onClick ? (
      <button
        type="button"
        className={glyphClassName}
        onClick={(e) => onClick(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>)}
        {...nameProps}
      >
        {glyphNode}
      </button>
    ) : (
      <span className={glyphClassName} role={hidden ? undefined : 'img'} {...nameProps}>
        {glyphNode}
      </span>
    );
  } else {
    // Default Avatar
    content = (
      <Avatar
        label="U" // Default label if none provided
        shape={shape}
        className={`sphere-avatar ${size} ${className}`}
        onClick={onClick}
        {...nameProps}
      />
    );
  }

  // Determine sparkle size based on avatar size - larger sizes for better visibility
  const getSparkleSize = (avatarSize: typeof size): number => {
    switch (avatarSize) {
      case 'xsmall': return 18;
      case 'small': return 24;
      case 'medium': return 28;
      default: return 32;
    }
  };
  const sparkleSize = getSparkleSize(size);

  // Handle sparkle click
  const handleSparkleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger avatar click
    onStatusClick?.(e);
  };

  // Render sparkle indicator if status is provided
  // Use proper semantic elements: button when clickable, span when not
  const renderSparkle = () => {
    if (!status) return null;

    if (onStatusClick) {
      return (
        <button
          type="button"
          className={`sphere-avatar-sparkle sphere-avatar-sparkle--${size} sphere-avatar-sparkle--clickable`}
          onClick={(e) => {
            handleSparkleClick(e);
          }}
          title="Click to view status details"
          aria-label="View status details"
        >
          <SparkleIcon color={getStatusColor(status)} size={sparkleSize} />
        </button>
      );
    }

    return (
      <span
        className={`sphere-avatar-sparkle sphere-avatar-sparkle--${size}`}
        aria-hidden="true"
      >
        <SparkleIcon color={getStatusColor(status)} size={sparkleSize} />
      </span>
    );
  };

  const sparkle = renderSparkle();

  return badge ? (
    <span className={`sphere-avatar-wrapper ${status ? 'sphere-avatar-wrapper--with-status' : ''}`}>
      {sparkle}
      {content}
      <Badge value={badge} severity="danger" className="sphere-avatar-badge" />
    </span>
  ) : (
    <span className={`sphere-avatar-wrapper ${status ? 'sphere-avatar-wrapper--with-status' : ''}`}>
      {sparkle}
      {content}
    </span>
  );
};

/**
 * Generate 2-character initials from a name.
 * "Marketing Ops" → "MO", "Sales" → "SA", "" → "?"
 */
function getInitials(name: string | undefined): string {
  if (!name || name.trim().length === 0) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.trim().substring(0, 2).toUpperCase();
}

/**
 * Generate a deterministic HSL background color from a string (e.g. sphereId).
 * Hashes the string into a hue (0-360) with fixed saturation and lightness
 * for a consistent, accessible palette.
 */
function getDeterministicColor(key: string | undefined): string {
  if (!key) return 'hsl(220, 40%, 55%)';
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (key.codePointAt(i) ?? 0) + ((hash << 5) - hash);
  }
  const hue = ((hash % 360) + 360) % 360;
  return `hsl(${hue}, 45%, 50%)`;
}

export default SphereAvatar;
